import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchOrderShelfLocations } from '../../../services/skuShelfIndexAPI';
import { fetchSkusWithNotes } from '../../../services/productWarehouseNotesAPI';
import { applyShelfLocationToOrderItem } from './shelfUtils';
import type { OrderItem, OrderShelfLocationMap } from '../../../types/apiTypes';

interface UsePickListShelfDataOptions {
  /** When false, skip mount fetch; caller must hydrate or call refreshShelfData. Default true. */
  readonly autoFetch?: boolean;
  readonly fetchLocations?: (skus: readonly string[]) => Promise<OrderShelfLocationMap>;
}

interface UsePickListShelfDataResult {
  readonly shelfLocationsMap: OrderShelfLocationMap;
  readonly skusWithNotes: Readonly<Record<string, true>>;
  readonly applyShelfDataToItems: (items: readonly OrderItem[]) => OrderItem[];
  readonly refreshShelfData: () => Promise<void>;
  readonly hydrateShelfData: (shelfMap: OrderShelfLocationMap, notesMap: Record<string, true>) => void;
}

export function usePickListShelfData(
  skus: readonly string[],
  options?: UsePickListShelfDataOptions,
): UsePickListShelfDataResult {
  const autoFetch = options?.autoFetch !== false;
  const fetchLocations = options?.fetchLocations ?? fetchOrderShelfLocations;
  const skuKey = useMemo(
    () => [...new Set(skus.map((sku) => sku.toUpperCase()).filter(Boolean))].sort().join('|'),
    [skus],
  );
  const normalizedSkus = useMemo(
    () => (skuKey ? skuKey.split('|') : []),
    [skuKey],
  );

  const [shelfLocationsMap, setShelfLocationsMap] = useState<OrderShelfLocationMap>({});
  const [skusWithNotes, setSkusWithNotes] = useState<Record<string, true>>({});

  const hydrateShelfData = useCallback((
    shelfMap: OrderShelfLocationMap,
    notesMap: Record<string, true>,
  ): void => {
    setShelfLocationsMap(shelfMap);
    setSkusWithNotes(notesMap);
  }, []);

  const refreshShelfData = useCallback(async (): Promise<void> => {
    if (normalizedSkus.length === 0) {
      setShelfLocationsMap({});
      setSkusWithNotes({});
      return;
    }

    const [nextShelfMap, nextNotesMap] = await Promise.all([
      fetchLocations(normalizedSkus),
      fetchSkusWithNotes(normalizedSkus),
    ]);
    setShelfLocationsMap(nextShelfMap);
    setSkusWithNotes(nextNotesMap);
  }, [normalizedSkus, fetchLocations]);

  useEffect(() => {
    if (!autoFetch) {
      return;
    }
    void refreshShelfData();
  }, [autoFetch, refreshShelfData]);

  const applyShelfDataToItems = useCallback((items: readonly OrderItem[]): OrderItem[] => {
    return items.map((item) => {
      const skuKey = item.sku?.toUpperCase();
      const loc = skuKey ? shelfLocationsMap[skuKey] ?? { shelfIndex: null, type: null } : { shelfIndex: null, type: null };
      return applyShelfLocationToOrderItem(item, loc);
    });
  }, [shelfLocationsMap]);

  return {
    shelfLocationsMap,
    skusWithNotes,
    applyShelfDataToItems,
    refreshShelfData,
    hydrateShelfData,
  };
}
